<script setup>
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { useDashboardStore } from '@/stores/dashboard'

const props = defineProps({
  onImport: {
    type: Function,
    required: true
  }
})

const emit = defineEmits(['modal-close'])
const toast = useToast()
const dashboardStore = useDashboardStore()

const input = ref('')
const importing = ref(false)
const error = ref('')
const parsed = ref(null)

const summary = computed(() => {
  if (!parsed.value) return null
  const widgets = parsed.value.widgets || []
  return {
    name: parsed.value.name || 'Untitled',
    description: parsed.value.description || '',
    widgetCount: widgets.length
  }
})

function handleInput() {
  error.value = ''
  parsed.value = null

  const trimmed = input.value.trim()
  if (!trimmed) return

  try {
    const bin = atob(trimmed)
    const bytes = Uint8Array.from(bin, c => c.charCodeAt(0))
    const json = new TextDecoder().decode(bytes)
    const data = JSON.parse(json)
    if (!data || typeof data !== 'object') {
      error.value = 'Invalid dashboard data'
      return
    }
    parsed.value = data
  } catch {
    error.value = 'Invalid base64 or JSON format'
  }
}

async function doImport() {
  if (!parsed.value || importing.value) return

  importing.value = true
  try {
    const imported = await dashboardStore.importDashboard(parsed.value)
    props.onImport(imported)
    emit('modal-close')
  } catch (e) {
    toast.error(e.message || 'Failed to import dashboard')
  } finally {
    importing.value = false
  }
}
</script>

<template>
  <div class="import-modal">
    <h3 class="modal-title">Import Dashboard</h3>
    <p class="modal-description">Paste an exported dashboard string below.</p>

    <template v-if="!parsed">
      <textarea
        v-model="input"
        class="import-textarea"
        placeholder="Paste base64 string here..."
        @input="handleInput"
      />

      <p v-if="error" class="import-error">{{ error }}</p>
    </template>

    <template v-if="summary">
      <div class="import-summary">
        <div class="summary-row">
          <span class="summary-label">Name</span>
          <span class="summary-value">{{ summary.name }}</span>
        </div>
        <div v-if="summary.description" class="summary-row">
          <span class="summary-label">Description</span>
          <span class="summary-value">{{ summary.description }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Widgets</span>
          <span class="summary-value">{{ summary.widgetCount }}</span>
        </div>
      </div>
    </template>

    <div class="modal-actions">
      <button
        class="btn-import"
        :disabled="!parsed || importing"
        @click="doImport"
      >
        <font-awesome-icon v-if="importing" :icon="['fas', 'spinner']" spin />
        <font-awesome-icon v-else :icon="['fas', 'file-import']" />
        {{ importing ? 'Importing...' : 'Import' }}
      </button>
      <button class="btn-close-modal" @click="emit('modal-close')">
        Cancel
      </button>
    </div>
  </div>
</template>

<style scoped>
.import-modal {
  width: 500px;
  max-width: 90vw;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
}

.modal-description {
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 0 0 1rem 0;
}

.import-textarea {
  width: 100%;
  height: 120px;
  background: var(--surface-overlay-2);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  padding: 0.75rem;
  color: var(--text-primary);
  font-family: monospace;
  font-size: 0.75rem;
  resize: vertical;
  word-break: break-all;
}

.import-textarea:focus {
  outline: none;
  border-color: rgba(115, 87, 210, 0.4);
}

.import-textarea::placeholder {
  color: var(--text-faint);
}

.import-error {
  color: var(--color-error);
  font-size: 0.8125rem;
  margin: 0.5rem 0 0 0;
}

.import-summary {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: rgba(115, 87, 210, 0.06);
  border: 1px solid rgba(115, 87, 210, 0.15);
  border-radius: var(--radius-md);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
}

.summary-row + .summary-row {
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.summary-label {
  font-size: 0.8125rem;
  color: var(--text-muted);
}

.summary-value {
  font-size: 0.8125rem;
  color: var(--text-primary);
  font-weight: 500;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1rem;
}

.btn-import {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--accent-soft-3);
  border: 1px solid rgba(115, 87, 210, 0.3);
  border-radius: var(--radius-sm);
  color: var(--accent);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

.btn-import:hover:not(:disabled) {
  background: var(--accent-soft-4);
  border-color: rgba(115, 87, 210, 0.5);
}

.btn-import:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-close-modal {
  padding: 0.5rem 1rem;
  background: var(--surface-overlay-2);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  color: var(--text-muted);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

.btn-close-modal:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary);
}
</style>
